import { createBrowserRouter, Navigate, useLocation } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { Executive } from "@/pages/Executive";
import { TopProjects } from "@/pages/TopProjects";
import { Capacity } from "@/pages/Capacity";
import { Portfolio } from "@/pages/Portfolio";
import { Departments } from "@/pages/Departments";
import { DepartmentPortfolio } from "@/pages/DepartmentPortfolio";
import { Intake } from "@/pages/Intake";
import { RequestProject } from "@/pages/RequestProject";
import { ServiceDeskDashboard } from "@/pages/ServiceDeskDashboard";
import { ServiceDeskTickets } from "@/pages/ServiceDeskTickets";
import { PersonDetail } from "@/pages/PersonDetail";
import { WeeklyUpdate } from "@/pages/WeeklyUpdate";
import { Help } from "@/pages/Help";
import { SynnergieSupport } from "@/pages/SynnergieSupport";
import { RequireRole } from "@/components/auth/RequireRole";
import { ApplicationSupportDashboard } from "@/pages/ApplicationSupportDashboard";
import { TeamRoster } from "@/pages/TeamRoster";
import { TeamView } from "@/pages/TeamView";
import { Settings } from "@/pages/Settings";
import { ProjectDetail } from "@/pages/ProjectDetail";
import { RoleCards } from "@/pages/RoleCards";
import { StaffingPlan } from "@/pages/StaffingPlan";
import { Wireframes } from "@/pages/Wireframes";
import { BriefPrintView } from "@/pages/BriefPrintView";
import { System } from "@/pages/System";
import { Admin } from "@/pages/Admin";
import { AgentsConsole } from "@/pages/AgentsConsole";
import { Profile } from "@/pages/Profile";
import { ErrorPage } from "@/pages/ErrorPage";
// v2 and Direct pages retired — routes redirect to unified pages

// Every old enhancement-queue URL lands on the Small Enhancements list
// (2026-10-01); the #ticket hash is carried through.
const ENHANCEMENTS = "/top-projects?view=enhancements";
function LegacyHelpdeskRedirect() {
  const location = useLocation();
  return (
    <Navigate
      to={{ pathname: "/top-projects", search: "?view=enhancements", hash: location.hash }}
      replace
    />
  );
}

export const router = createBrowserRouter([
  // Print routes live OUTSIDE AppShell so no sidebar/topbar appears.
  // Accessed via "Export PDF" on the Brief tab; opens in new tab.
  { path: "portfolio/:projectId/brief/print", element: <BriefPrintView /> },
  {
    element: <AppShell />,
    errorElement: <ErrorPage />,
    children: [
      { index: true, element: <Navigate to="/executive" replace /> },
      { path: "executive", element: <Executive /> },
      { path: "weekly-update", element: <WeeklyUpdate /> },
      // In-app Help (2026-09-28): the Confluence guide, mirrored.
      { path: "help", element: <Help /> },
      { path: "help/:slug", element: <Help /> },
      // The business's prioritization board (Top 10 / Just Do Its /
      // Next up / Unscoped / Paused) — see lib/priorityTier.ts.
      { path: "top-projects", element: <TopProjects /> },
      // /review retired 2026-09-10 — the Top Projects board (cards +
      // health dots + ranked bands) is the exec meeting surface now.
      { path: "review", element: <Navigate to="/top-projects" replace /> },
      { path: "capacity", element: <Capacity /> },
      // Flow (work-tracking boards): open to every signed-in user; each
      // board's own access decides what they see. Loaded on demand so the
      // rest of the planner doesn't download it up front.
      { path: "flow", lazy: async () => ({ Component: (await import("@/pages/Flow")).Flow }) },
      { path: "flow/my-work", lazy: async () => ({ Component: (await import("@/pages/FlowMyWork")).FlowMyWork }) },
      { path: "flow/boards/:boardId", lazy: async () => ({ Component: (await import("@/pages/FlowBoard")).FlowBoard }) },
      {
        path: "flow/boards/:boardId/setup",
        lazy: async () => ({ Component: (await import("@/pages/FlowBoardSetup")).FlowBoardSetup }),
      },
      // Time (Jira worklog rollup) RETIRED 2026-09-15.
      { path: "time", element: <Navigate to="/capacity" replace /> },
      { path: "portfolio", element: <Portfolio /> },
      // Portfolio Health + its drill-down RETIRED 2026-09-15 — the
      // Top Projects board carries health at exec altitude.
      { path: "portfolio/health", element: <Navigate to="/portfolio" replace /> },
      { path: "portfolio/health/drill", element: <Navigate to="/portfolio" replace /> },
      { path: "departments", element: <Departments /> },
      { path: "departments/:slug", element: <DepartmentPortfolio /> },
      // Dev Date Compliance RETIRED 2026-09-15.
      { path: "dashboards/dev-compliance", element: <Navigate to="/executive" replace /> },
      { path: "intake", element: <Intake /> },
      { path: "request", element: <RequestProject /> },
      { path: "service-desk", element: <ServiceDeskDashboard /> },
      { path: "service-desk/tickets", element: <ServiceDeskTickets /> },
      { path: "people/:name", element: <PersonDetail /> },
      // The Enhancements page retired 2026-10-01: the Small Enhancements
      // list lives on Top Projects. /triage went there via /enhancements.
      { path: "enhancements", element: <Navigate to={ENHANCEMENTS} replace /> },
      { path: "triage", element: <Navigate to={ENHANCEMENTS} replace /> },
      // The triage inbox lived at /helpdesk until 2026-08-14. Redirect
      // keeps old links working, preserving ?tab= and the #ticket hash.
      { path: "helpdesk", element: <LegacyHelpdeskRedirect /> },
      // The BA / Application Support queue page was retired 2026-09-10
      // (Brett: not needed). Old links land on the triage inbox; the
      // health dashboard below stays.
      { path: "application-support", element: <Navigate to={ENHANCEMENTS} replace /> },
      { path: "application-support/dashboard", element: <ApplicationSupportDashboard /> },
      // Synnergie support metrics (2026-09-29): PMO/Admin only.
      { path: "dashboards/synnergie-support", element: <RequireRole min="pmo"><SynnergieSupport /></RequireRole> },
      // Synnergie Workload RETIRED 2026-09-15 — the team page covers
      // who/what; the 655 mirrored SSE rows stay in the DB, unsurfaced.
      { path: "synnergie", element: <Navigate to="/teams/synnergie" replace /> },
      { path: "roles", element: <RoleCards /> },
      { path: "staffing", element: <Navigate to="/top-projects" replace /> },
      { path: "portfolio/:projectId", element: <ProjectDetail /> },
      { path: "roster", element: <TeamRoster /> },
      { path: "teams/:teamSlug", element: <TeamView /> },
      // /planning retired 2026-09-04 (bench + what-if) — the board owns
      // priority order; dates/staffing live on project pages.
      { path: "planning", element: <Navigate to="/top-projects" replace /> },
      // Role-gated pages (2026-09-28): the server refuses the writes; the
      // guard keeps the pages out of sight and never shows a blank page.
      { path: "settings", element: <RequireRole min="admin"><Settings /></RequireRole> },
      { path: "system", element: <RequireRole min="admin"><System /></RequireRole> },
      { path: "admin", element: <RequireRole min="admin"><Admin /></RequireRole> },
      // Flag-gated (can_view_agents): the API 404s without the flag, so
      // this route renders an error state rather than data for anyone
      // who navigates here by hand.
      { path: "agents", element: <AgentsConsole /> },
      { path: "profile", element: <Profile /> },
      // Hidden — not in Sidebar. Reachable only via direct URL.
      { path: "wireframes", element: <Wireframes /> },
      // Legacy routes — redirect to unified pages
      { path: "v2/capacity", element: <Navigate to="/capacity" replace /> },
      { path: "v2/portfolio", element: <Navigate to="/portfolio" replace /> },
      { path: "direct/capacity", element: <Navigate to="/capacity" replace /> },
      // Catch-all — any unmatched path renders the 404 inside the shell.
      { path: "*", element: <ErrorPage /> },
    ],
  },
]);
